// The KPI strips (D104). Every number is ListOccurrences' own state_counts
// (the named denominators, taken over the window before any state filter),
// never a client recount, and every tile links to the rows it counts.
//
//   - ProgramKpiStrip: the program's roll-up — the contract period containing
//     today (Base / OPn), with a picker for past periods (statuses still as of
//     today), plus the open items carried over from earlier windows.
//   - TeamKpiStrip: one team's own current window (its in-force SOW's
//     interval, else its exercise line's): the same five states, plus
//     "Awaiting approval (n)" (D112 #8), linking to that team's approvals list.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraEmptyState, SpecraEyebrow, SpecraLink, SpecraMetricStrip, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { OccurrenceWindow, useOccurrenceDecisions, useOccurrences } from '../hooks';
import { cmSpan, todayISO } from '../lib/format';
import { useCmLookups } from '../lookups';
import { awaitingHref } from './AwaitingApproval';
const TILES = [
    {
        state: 'late',
        label: 'Late',
        tone: 'danger'
    },
    {
        state: 'awaiting_review',
        label: 'Awaiting review',
        tone: 'info'
    },
    {
        state: 'rejected',
        label: 'Rejected',
        tone: 'danger'
    },
    {
        state: 'due_soon',
        label: 'Due next 14 days',
        tone: 'warning'
    },
    {
        state: 'approved',
        label: 'Approved',
        tone: 'success'
    }
];
function hasAny(r) {
    return !!r && Object.values(r.stateCounts ?? {}).some((n)=>n > 0);
}
/**
 * A strip's props: one tile per state, each a router link to the rows it
 * counts. `states` narrows the five (the contract-wide view shows four).
 */ export function stripProps({ counts, hrefFor, testId, tileTestId, states, extra = [] }) {
    const n = (state)=>counts?.[state] ?? 0;
    const metrics = [
        ...TILES.filter((t)=>!states || states.includes(t.state)).map((t)=>({
                id: t.state,
                label: t.label,
                value: n(t.state),
                tone: n(t.state) > 0 ? t.tone : 'neutral',
                href: hrefFor(t.state)
            })),
        ...extra
    ];
    return {
        'data-testid': testId,
        metrics,
        renderLink: renderRouterLink,
        getItemTestId: (m)=>m.id === 'awaiting_approval' ? Testids.cmWorkstream.awaitingApproval : tileTestId,
        getItemProps: (m)=>({
                'data-state': m.id
            })
    };
}
/** Placeholder tiles while a strip's counts load; no testids, so nothing reads them as counts. */ export const LOADING_STRIP = {
    metrics: [],
    loading: true
};
export function ProgramKpiStrip({ programId, periodId, onPeriodChange }) {
    const lk = useCmLookups(programId);
    const q = useOccurrences(programId, periodId ? {
        window: OccurrenceWindow.CONTRACT_PERIOD,
        periodOfPerformanceId: periodId,
        pageSize: 1
    } : {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        pageSize: 1
    });
    const r = q.data;
    const today = todayISO();
    // Which period is current is confirmed only when every period is dated and
    // the server's window found one covering today (it counts an exercised
    // period only). Otherwise naming one from dates alone can call an
    // unexercised option current while an undated base actually is.
    const unconfirmed = !periodId && lk.periods.length > 0 && (lk.periods.some((p)=>!p.start || !p.end) || r?.window?.basis === 'none');
    const current = unconfirmed ? undefined : lk.periods.find((p)=>p.start && p.start <= today && (!p.end || today <= p.end));
    const viewing = periodId ? lk.periodById.get(periodId) : current;
    const ended = !!viewing?.end && viewing.end < today;
    const base = `/delivery/programs/${programId}/deliverables`;
    const hrefFor = (state)=>`${base}?state=${state}${periodId ? `&period=${periodId}` : ''}`;
    const label = unconfirmed ? /*#__PURE__*/ _jsxs("span", {
        "data-testid": Testids.cmKpi.periodUnconfirmed,
        children: [
            "Current period not confirmed:",
            ' ',
            /*#__PURE__*/ _jsx(SpecraLink, {
                href: `/delivery/programs/${programId}/periods`,
                renderLink: renderRouterLink,
                children: "set period dates"
            })
        ]
    }) : viewing ? `${viewing.name} · ${cmSpan(viewing.start, viewing.end)}` : 'No contract period covers today';
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "aria-label": "Deliverables this period",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraEyebrow, {
                        children: "Deliverables"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "caption",
                        tone: "muted",
                        children: label
                    }),
                    periodId && ended ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "neutral",
                        "data-testid": Testids.cmKpi.viewingPast,
                        children: [
                            "Viewing ",
                            viewing?.name,
                            " (ended)"
                        ]
                    }) : null,
                    lk.periods.length > 0 ? /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Contract period",
                        className: "ml-auto w-48",
                        value: periodId,
                        onChange: (e)=>onPeriodChange(e.target.value),
                        "data-testid": Testids.cmKpi.periodPicker,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: current ? `${current.name} (current)` : unconfirmed ? 'Current period (not confirmed)' : 'Current period'
                            }),
                            lk.periods.filter((p)=>p.id !== current?.id && p.start && p.end).map((p)=>/*#__PURE__*/ _jsx("option", {
                                    value: p.id,
                                    children: p.name
                                }, p.id))
                        ]
                    }) : null
                ]
            }),
            q.isLoading ? /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                ...LOADING_STRIP
            }) : !hasAny(r) ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                size: "compact",
                "data-testid": Testids.cmKpi.empty,
                title: "No dates yet.",
                description: "Deliverable dates appear once a deliverable has a rule and the period or exercise it covers is on the contract.",
                action: /*#__PURE__*/ _jsx(SpecraLink, {
                    href: base,
                    renderLink: renderRouterLink,
                    children: "Open Deliverables"
                })
            }) : /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                ...stripProps({
                    counts: r?.stateCounts,
                    hrefFor,
                    testId: Testids.cmKpi.strip,
                    tileTestId: Testids.cmKpi.tile
                })
            }),
            r?.window?.basis === 'none' && !unconfirmed ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                "data-testid": Testids.cmKpi.noPeriod,
                children: "No contract period covers today; showing open items."
            }) : null,
            r && r.carriedOver > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                "data-testid": Testids.cmKpi.carriedOver,
                children: [
                    "Includes ",
                    r.carriedOver,
                    " open ",
                    r.carriedOver === 1 ? 'item' : 'items',
                    " carried over from an earlier period."
                ]
            }) : null
        ]
    });
}
/**
 * One team's strip over its own current window: the strip's props (or the
 * loading tiles) and the line naming that window. The team page hands the
 * props to its dashboard shell as its metrics.
 */ export function useTeamKpi(programId, workstreamId, { enabled = true } = {}) {
    const q = useOccurrences(programId, {
        workstreamId,
        window: OccurrenceWindow.WORKSTREAM_CURRENT_WINDOW,
        pageSize: 1
    }, {
        enabled
    });
    const pending = useOccurrenceDecisions(programId, {
        workstreamId,
        openOnly: true
    }, {
        enabled
    });
    const n = pending.data?.decisions.length ?? 0;
    const base = `/delivery/programs/${programId}/workstreams/${workstreamId}`;
    const r = q.data;
    const strip = q.isLoading ? LOADING_STRIP : stripProps({
        counts: r?.stateCounts,
        hrefFor: (state)=>`${base}?state=${state}`,
        testId: Testids.cmWorkstream.strip,
        tileTestId: Testids.cmWorkstream.stripTile,
        extra: [
            {
                id: 'awaiting_approval',
                label: 'Awaiting approval',
                value: n,
                tone: n > 0 ? 'warning' : 'neutral',
                note: 'Proposals a second person must approve',
                href: awaitingHref(programId, {
                    team: workstreamId
                })
            }
        ]
    });
    const windowNote = r?.window && r.window.basis !== 'none' ? /*#__PURE__*/ _jsxs(SpecraText, {
        as: "p",
        size: "caption",
        tone: "muted",
        children: [
            "This team’s window: ",
            cmSpan(r.window.startOn, r.window.endOn),
            r.carriedOver > 0 ? ` · ${r.carriedOver} open carried over` : ''
        ]
    }) : r?.window?.basis === 'none' ? /*#__PURE__*/ _jsx(SpecraText, {
        as: "p",
        size: "caption",
        tone: "muted",
        children: "No current window: showing this team’s open items."
    }) : null;
    return {
        strip,
        windowNote
    };
}
export function TeamKpiStrip({ programId, workstreamId }) {
    const { strip, windowNote } = useTeamKpi(programId, workstreamId);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                ...strip
            }),
            windowNote
        ]
    });
}
